/**
 * Métadonnées des blocs de programme (workout structuré + rééducation) persistées
 * dans `groupDescription`. Le format complet d'une `groupDescription` est :
 *   `@@color:N@@` (`@@client:id@@`?) `@@set:…@@`? `@@rehab:…@@`? `@@workout:…@@`? <instruction libre>
 *
 * Le résumé workout et l'instruction libre doivent apparaître en **entête** du bloc
 * côté client, jamais dans la liste des étapes à valider.
 */
import { stripGroupColorMeta } from "./block-color";

export type CustomWorkoutType =
  | "AMRAP"
  | "EMOM"
  | "FOR_TIME"
  | "CIRCUIT_TRAINING";

export const CUSTOM_WORKOUT_TYPES: CustomWorkoutType[] = [
  "AMRAP",
  "EMOM",
  "FOR_TIME",
  "CIRCUIT_TRAINING",
];

export const WORKOUT_META_RE = /@@workout:([\s\S]*?)@@/;
export const SET_META_RE = /@@set:([\s\S]*?)@@/;
export const REHAB_META_RE = /@@rehab:([\s\S]*?)@@/;
export const MEDIA_META_RE = /@@media:([\s\S]*?)@@/;

/**
 * Média attaché à un bloc. Plusieurs médias peuvent coexister :
 * - `exercise` : référence un exercice de la bibliothèque (`exerciseId`), la
 *   fiche complète étant résolue côté API. `url` sert d'aperçu (image/vidéo).
 * - `link` : un lien libre (YouTube, Vimeo, image, URL…).
 */
export type BlockMedia = {
  kind: "exercise" | "link";
  url: string;
  exerciseId?: string;
  title?: string;
};

/** Forme compacte persistée dans `@@media:[...]@@` (clés courtes pour limiter la taille). */
type SerializedBlockMedia = {
  k: "ex" | "link";
  u?: string;
  x?: string;
  t?: string;
};

const serializeBlockMedia = (media: BlockMedia): SerializedBlockMedia | null => {
  const url = media.url?.trim() ?? "";
  const exerciseId = media.exerciseId?.trim();
  const title = media.title?.trim();
  if (media.kind === "exercise") {
    if (!exerciseId) return null;
    return {
      k: "ex",
      ...(url ? { u: url } : {}),
      x: exerciseId,
      ...(title ? { t: title } : {}),
    };
  }
  if (!url) return null;
  return { k: "link", u: url, ...(title ? { t: title } : {}) };
};

const deserializeBlockMedia = (
  entry: SerializedBlockMedia,
): BlockMedia | null => {
  const url = entry.u?.trim() ?? "";
  const exerciseId = entry.x?.trim();
  const title = entry.t?.trim();
  if (entry.k === "ex") {
    if (!exerciseId) return null;
    return { kind: "exercise", url, exerciseId, ...(title ? { title } : {}) };
  }
  if (!url) return null;
  return { kind: "link", url, ...(title ? { title } : {}) };
};

export const encodeBlockMediaMeta = (medias?: BlockMedia[]): string => {
  if (!medias?.length) return "";
  const cleaned = medias
    .map(serializeBlockMedia)
    .filter((entry): entry is SerializedBlockMedia => entry != null);
  if (cleaned.length === 0) return "";
  return `@@media:${JSON.stringify(cleaned)}@@`;
};

export const parseBlockMediaMeta = (
  description?: string | null,
): BlockMedia[] => {
  const match = description?.match(MEDIA_META_RE);
  if (!match?.[1]) return [];
  try {
    const parsed = JSON.parse(match[1]) as SerializedBlockMedia[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(deserializeBlockMedia)
      .filter((entry): entry is BlockMedia => entry != null);
  } catch {
    return [];
  }
};

export const stripBlockMediaMeta = (
  description?: string | null,
): string | null => {
  if (!description) return null;
  const stripped = description.replace(MEDIA_META_RE, "").trim();
  return stripped || null;
};

export const encodeWorkoutMeta = (
  workoutType?: CustomWorkoutType | "",
  workoutParams?: Record<string, string>,
): string => {
  if (!workoutType) return "";
  const payload = JSON.stringify({
    type: workoutType,
    params: workoutParams ?? {},
  });
  return `@@workout:${payload}@@`;
};

export const parseWorkoutMeta = (
  description?: string | null,
): {
  workoutType?: CustomWorkoutType;
  workoutParams?: Record<string, string>;
} => {
  const match = description?.match(WORKOUT_META_RE);
  if (!match?.[1]) return {};
  try {
    const parsed = JSON.parse(match[1]) as {
      type?: CustomWorkoutType;
      params?: Record<string, string>;
    };
    if (!parsed.type || !CUSTOM_WORKOUT_TYPES.includes(parsed.type)) {
      return {};
    }
    return {
      workoutType: parsed.type,
      workoutParams: parsed.params ?? {},
    };
  } catch {
    return {};
  }
};

export const stripWorkoutMeta = (
  description?: string | null,
): string | null => {
  if (!description) return null;
  const stripped = description.replace(WORKOUT_META_RE, "").trim();
  return stripped || null;
};

export const stripRehabMeta = (
  description?: string | null,
): string | null => {
  if (!description) return null;
  const stripped = description.replace(REHAB_META_RE, "").trim();
  return stripped || null;
};

export const stripSetMeta = (
  description?: string | null,
): string | null => {
  if (!description) return null;
  const stripped = description.replace(SET_META_RE, "").trim();
  return stripped || null;
};

/**
 * Ligne résumé compacte persistée comme première étape du bloc (séparateur « · »).
 * Conservée à l'identique pour pouvoir filtrer cette étape côté client/pro.
 */
export const buildCustomWorkoutSummaryLine = (
  workoutType: CustomWorkoutType,
  params: Record<string, string> = {},
): string => {
  switch (workoutType) {
    case "AMRAP":
      return params.duration?.trim()
        ? `AMRAP · ${params.duration.trim()}`
        : "AMRAP";
    case "EMOM": {
      const parts: string[] = [];
      if (params.every?.trim()) parts.push(`toutes les ${params.every.trim()}`);
      if (params.forDuration?.trim()) {
        parts.push(`pendant ${params.forDuration.trim()}`);
      }
      return parts.length > 0 ? `EMOM · ${parts.join(" · ")}` : "EMOM";
    }
    case "FOR_TIME":
      return params.duration?.trim()
        ? `FOR TIME · ${params.duration.trim()}`
        : "FOR TIME";
    case "CIRCUIT_TRAINING": {
      const parts: string[] = [];
      if (params.rounds?.trim()) parts.push(`${params.rounds.trim()} rounds`);
      if (params.effortDuration?.trim()) {
        parts.push(`effort ${params.effortDuration.trim()}`);
      }
      if (params.recovery?.trim()) {
        parts.push(`récup ${params.recovery.trim()}`);
      }
      if (params.interRoundRecovery?.trim()) {
        parts.push(`inter-round ${params.interRoundRecovery.trim()}`);
      }
      return parts.length > 0
        ? `Circuit · ${parts.join(" · ")}`
        : "Circuit Training";
    }
    default:
      return "";
  }
};

/**
 * Ajoute l'unité « mn » à une durée mm:ss. Les minutes pleines sont condensées
 * (« 1:00 » → « 1 mn », « 15:00 » → « 15 mn »), sinon on garde mm:ss (« 1:30 mn »).
 */
const formatWorkoutTimeWithUnit = (raw: string): string => {
  const value = raw.trim();
  const [minPart, secPart] = value.split(":");
  const minutes = Number.parseInt(minPart ?? "", 10);
  const seconds = Number.parseInt(secPart ?? "0", 10);
  if (Number.isFinite(minutes) && (!secPart || seconds === 0)) {
    return `${minutes} mn`;
  }
  return `${value} mn`;
};

/**
 * Entête lisible affichée côté client (séparateur « - », unités de temps explicites).
 * Ex. EMOM → « EMOM - toutes les 1:00 minute - pendant 15:00 minutes ».
 */
export const buildCustomWorkoutHeadline = (
  workoutType: CustomWorkoutType,
  params: Record<string, string> = {},
): string => {
  switch (workoutType) {
    case "AMRAP":
      return params.duration?.trim()
        ? `AMRAP - ${formatWorkoutTimeWithUnit(params.duration)}`
        : "AMRAP";
    case "EMOM": {
      const parts: string[] = [];
      if (params.every?.trim()) {
        parts.push(`toutes les ${formatWorkoutTimeWithUnit(params.every)}`);
      }
      if (params.forDuration?.trim()) {
        parts.push(`pendant ${formatWorkoutTimeWithUnit(params.forDuration)}`);
      }
      return parts.length > 0 ? `EMOM - ${parts.join(" - ")}` : "EMOM";
    }
    case "FOR_TIME":
      return params.duration?.trim()
        ? `FOR TIME - ${formatWorkoutTimeWithUnit(params.duration)}`
        : "FOR TIME";
    case "CIRCUIT_TRAINING": {
      const parts: string[] = [];
      if (params.rounds?.trim()) parts.push(`${params.rounds.trim()} rounds`);
      if (params.effortDuration?.trim()) {
        parts.push(`effort ${formatWorkoutTimeWithUnit(params.effortDuration)}`);
      }
      if (params.recovery?.trim()) {
        parts.push(`récup ${formatWorkoutTimeWithUnit(params.recovery)}`);
      }
      if (params.interRoundRecovery?.trim()) {
        parts.push(
          `inter-round ${formatWorkoutTimeWithUnit(params.interRoundRecovery)}`,
        );
      }
      return parts.length > 0
        ? `Circuit Training - ${parts.join(" - ")}`
        : "Circuit Training";
    }
    default:
      return "";
  }
};

/**
 * Décode une `groupDescription` en séparant le résumé workout structuré de
 * l'instruction libre, après avoir retiré toutes les métadonnées techniques.
 */
export const parseBlockGroupMeta = (
  description?: string | null,
): {
  instruction: string | null;
  workoutType?: CustomWorkoutType;
  workoutParams?: Record<string, string>;
  medias: BlockMedia[];
} => {
  const withoutColor = stripGroupColorMeta(description) ?? "";
  const { workoutType, workoutParams } = parseWorkoutMeta(withoutColor);
  const medias = parseBlockMediaMeta(withoutColor);
  const withoutSet = stripSetMeta(withoutColor) ?? "";
  const withoutRehab = stripRehabMeta(withoutSet) ?? "";
  const withoutWorkout = stripWorkoutMeta(withoutRehab) ?? "";
  const instruction = stripBlockMediaMeta(withoutWorkout);
  return { instruction, workoutType, workoutParams, medias };
};
